import { Outlet, useLocation, Navigate } from 'react-router-dom'
import { getCurrentUser } from '@/utils/auth/UserProvider'
import { isOrgCreationDisabled } from '@/utils/utils'
import WaitlistGreeting from './WaitlistGreeting'
import Loading from './Loading'
import ErrorMessage from './ErrorMessage'
import { useEffect, useState } from 'react'
import {
  getPostLoginRedirect,
  clearPostLoginRedirect,
  markPostLoginRedirectUsed,
  isPostLoginRedirectUsed,
} from '@/utils/postLoginRedirect'

export default function AuthenticatedRoute() {
  const { isLoading, user, orgs, token, isError } = getCurrentUser()
  // Initialise from actual state — if user data is already cached, isLoading
  // is false so we skip the loading gate immediately. Prevents a one-frame
  // flash of <Loading /> when switching between route branches.
  const [loading, setLoading] = useState(isLoading)
  const location = useLocation()
  const postLoginRedirect = getPostLoginRedirect()

  const isInviteFlow =
    location.pathname.startsWith('/invite') ||
    !!postLoginRedirect?.startsWith('/invite')

  const shouldRedirectToInvite =
    !!user &&
    !!token &&
    !!postLoginRedirect?.startsWith('/invite') &&
    location.pathname !== postLoginRedirect &&
    !isPostLoginRedirectUsed()

  useEffect(() => {
    if (!isLoading) {
      setLoading(false)
    }
  }, [isLoading])

  // clear localStorage when user lands on invite page (redirect has served its purpose)
  //  if there's a verification error accepting this will be set again there
  // Keep sessionStorage flag to prevent repeated redirects if user navigates away
  useEffect(() => {
    if (user && token && location.pathname.startsWith('/invite')) {
      clearPostLoginRedirect()
    }
  }, [user, token, location.pathname])

  // Mark the redirect as used AFTER commit (never during render) — render-phase
  // mutation of sessionStorage caused this block to self-invalidate on a second
  // render of the same commit, handing control to the no-org gate and sending
  // the user to /onboarding instead.
  useEffect(() => {
    if (shouldRedirectToInvite) {
      markPostLoginRedirectUsed()
    }
  }, [shouldRedirectToInvite])

  if (isLoading || loading) {
    return <Loading />
  }

  if (isError) {
    return <ErrorMessage />
  }

  // Name gate: nameless users must complete their profile first.
  // Invite flow is exempt — InvitationPage shows its own name modal so the
  // invitee reaches the accept/decline screen without a full-page detour.
  if (user && token && (!user.first_name || !user.last_name) && !isInviteFlow) {
    if (location.pathname === '/onboarding/name') {
      return <Outlet />
    }
    return <Navigate to="/onboarding/name" replace />
  }

  if (shouldRedirectToInvite) {
    return <Navigate to={postLoginRedirect!} replace />
  }

  // If org creation is disabled through the environment variable, show the waitlist greeting
  if (isOrgCreationDisabled() && user && (!orgs || orgs.length === 0)) {
    console.log('disabled by environment variable')
    return <WaitlistGreeting user_email={user.email} />
  }

  // If user has no orgs (but org creation is allowed), send them to v2 onboarding
  const hasOrganisation = (orgs && orgs.length > 0) || user?.selected_org_uuid

  if (user && token && !hasOrganisation) {
    // Allow onboarding and invite routes through — v2 handles org creation
    if (location.pathname.startsWith('/onboarding') || location.pathname.startsWith('/invite')) {
      return <Outlet />
    }

    // Everything else redirects to onboarding
    return <Navigate to="/onboarding" replace />
  }

  return <Outlet />
}
